import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { useDetectClickOutside } from "react-detect-click-outside"
import { useTranslation } from "react-i18next"
import styled from "styled-components"
import Icon from "../Icon"
import { BREAKPOINTS, TAP_MIN, Z } from "../GlobalTheme"

const Container = styled.div`
  position: relative;
  display: inline-block;
`

const Trigger = styled.div`
  display: flex;
  cursor: pointer;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    min-height: ${TAP_MIN}px;
    align-items: center;
  }
`

const TriggerLabel = styled.p`
  font-weight: 700;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_900};
  margin: 0px;
  margin-top: 12px;
  margin-left: 8px;
  white-space: nowrap;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    margin-top: 0px;
  }
`

const Panel = styled.div<{ $up: boolean, $alignRight: boolean }>`
  position: absolute;
  ${props => props.$up ? 'bottom: calc(100% + 8px);' : 'top: calc(100% + 8px);'}
  ${props => props.$alignRight ? 'right: 0px;' : 'left: 0px;'}
  display: flex;
  box-sizing: border-box;
  border-radius: 12px;
  border: 1px solid ${props => props.theme.colors.gray_300};
  background-color: ${props => props.theme.colors.white};
  box-shadow: 4px 8px 32px 0px #00000033;
  z-index: ${Z.dropdown};
  max-width: calc(100vw - 24px);

  /* Presets sit beside the calendar on a wide screen and above it on a phone, where there is no room
     for a side column next to a 7-column grid of 44px cells. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    flex-direction: column;
  }
`

const PresetColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  border-right: 1px solid ${props => props.theme.colors.gray_300};

  @media (max-width: ${BREAKPOINTS.shell}px) {
    flex-direction: row;
    flex-wrap: wrap;
    border-right: none;
    border-bottom: 1px solid ${props => props.theme.colors.gray_300};
  }
`

const PresetButton = styled.button<{ $selected: boolean }>`
  font-family: inherit;
  font-weight: ${props => props.$selected ? '700' : '500'};
  font-size: 12px;
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
  border: none;
  border-radius: 8px;
  padding: 10px 12px;
  color: ${props => props.$selected ? props.theme.colors.white : props.theme.colors.gray_900};
  background-color: ${props => props.$selected ? props.theme.colors.primary : 'transparent'};

  @media (max-width: ${BREAKPOINTS.shell}px) {
    min-height: ${TAP_MIN}px;
    font-size: 13px;
  }

  &:hover {
    background-color: ${props => props.$selected ? props.theme.colors.primary : props.theme.colors.gray_200};
  }
`

const CalendarColumn = styled.div`
  padding: 12px;
`

const MonthHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

/* The month and the year are each a drill-down into their own grid, so they have to read as controls
   rather than as a caption. The year-range caption in the year view uses this same element on purpose:
   rendering it as a <p> picked up global paragraph styling and made that header 8px taller. */
const TitleButton = styled.button`
  font-family: inherit;
  font-weight: 700;
  font-size: 13px;
  color: ${props => props.theme.colors.gray_900};
  background: transparent;
  border: none;
  cursor: pointer;
  border-radius: 6px;
  /* The year-range caption renders as a <p>, which would otherwise bring default paragraph margins
     and the inherited line-height with it and make the header 8px taller than in the day view. */
  margin: 0px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 6px;
  text-transform: capitalize;
  white-space: nowrap;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    min-height: ${TAP_MIN}px;
  }

  &:hover {
    background-color: ${props => props.theme.colors.gray_200};
  }
`

/* Keeps the panel from resizing as the views swap under the same header. */
const GridArea = styled.div`
  /* Sized to the day grid - seven cells across, six rows plus the weekday row - so stepping into the
     month or year view does not resize the panel under the cursor. */
  min-width: 238px;
  min-height: 224px;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    min-width: 308px;
    min-height: 286px;
  }
`

const CoarseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  padding-top: 4px;
`

const CoarseCell = styled.button<{ $selected: boolean, $current: boolean }>`
  font-family: inherit;
  font-size: 12px;
  font-weight: ${props => props.$selected ? '700' : '500'};
  border: none;
  cursor: pointer;
  border-radius: 8px;
  height: 46px;
  text-transform: capitalize;
  color: ${props => props.$selected ? props.theme.colors.white : props.theme.colors.gray_900};
  background-color: ${props => props.$selected ? props.theme.colors.primary : 'transparent'};
  outline: ${props => props.$current && !props.$selected ? `1px solid ${props.theme.colors.primary}` : 'none'};
  outline-offset: -1px;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    height: ${TAP_MIN}px;
    font-size: 13px;
  }

  &:hover {
    background-color: ${props => props.$selected ? props.theme.colors.primary : props.theme.colors.gray_200};
  }
`

const NavButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0px;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    width: ${TAP_MIN}px;
    height: ${TAP_MIN}px;
  }

  &:hover {
    background-color: ${props => props.theme.colors.gray_200};
  }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
`

const WeekdayCell = styled.p`
  font-weight: 600;
  font-size: 10px;
  color: ${props => props.theme.colors.gray_500};
  margin: 0px;
  text-align: center;
  padding-bottom: 6px;
  text-transform: uppercase;
`

/* One flat cell per day. The range band is painted by each cell's own background rather than a
   separate layer, so the month stays a plain grid with nothing absolutely positioned over it. */
const DayCell = styled.button<{ $outside: boolean, $edge: boolean, $inRange: boolean, $today: boolean }>`
  font-family: inherit;
  font-size: 12px;
  font-weight: ${props => props.$edge ? '700' : '500'};
  border: none;
  cursor: pointer;
  padding: 0px;
  width: 34px;
  height: 34px;
  border-radius: ${props => props.$edge ? '8px' : '0px'};
  color: ${props => props.$edge
    ? props.theme.colors.white
    : props.$outside ? props.theme.colors.gray_500 : props.theme.colors.gray_900};
  background-color: ${props => props.$edge
    ? props.theme.colors.primary
    : props.$inRange ? props.theme.colors.gray_200 : 'transparent'};
  outline: ${props => props.$today && !props.$edge ? `1px solid ${props.theme.colors.primary}` : 'none'};
  outline-offset: -1px;
  opacity: ${props => props.$outside ? 0.45 : 1};

  @media (max-width: ${BREAKPOINTS.shell}px) {
    width: ${TAP_MIN}px;
    height: ${TAP_MIN}px;
    font-size: 13px;
  }

  &:hover {
    background-color: ${props => props.$edge ? props.theme.colors.primary : props.theme.colors.gray_300};
  }
`

const Hint = styled.p`
  font-weight: 500;
  font-size: 11px;
  color: ${props => props.theme.colors.gray_500};
  margin: 8px 0px 0px 0px;
  text-align: center;
`

/** Presets are whole days back from today, matching the ticket: last day, week, month, year. */
const PRESETS = [
  { key: 'dateRangePicker_lastDay', days: 1 },
  { key: 'dateRangePicker_lastWeek', days: 7 },
  { key: 'dateRangePicker_lastMonth', days: 30 },
  { key: 'dateRangePicker_lastYear', days: 365 },
]

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate())
const addDays = (d: Date, n: number) => { const x = startOfDay(d); x.setDate(x.getDate() + n); return x }
const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
const asDayLabel = (d: Date) => `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`

/** Monday-first unless the locale says otherwise; weekInfo is missing in some browsers. */
function firstDayOfWeek(locale: string): number {
  try {
    // @ts-expect-error weekInfo is not in the TS lib definitions yet
    const info = new Intl.Locale(locale).weekInfo
    if (info && typeof info.firstDay === 'number') return info.firstDay % 7
  } catch { /* unsupported locale, or no weekInfo */ }
  return 1
}

/** The 6x7 block of days covering the given month, padded with its neighbours. */
function buildMonthGrid(viewMonth: Date, weekStart: number): Date[] {
  const first = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1)
  const offset = (first.getDay() - weekStart + 7) % 7
  const gridStart = addDays(first, -offset)
  return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i))
}

/** The horizontal span the element can be seen in: the window, narrowed by every ancestor that clips. */
function visibleSpan(el: HTMLElement): { left: number, right: number } {
  let left = 0
  let right = window.innerWidth
  for (let node = el.parentElement; node; node = node.parentElement) {
    if (getComputedStyle(node).overflowX === 'visible') continue
    const rect = node.getBoundingClientRect()
    left = Math.max(left, rect.left)
    right = Math.min(right, rect.right)
  }
  return { left, right }
}

export default function DateRangePickerWithPresets({ onSelect, defaultDate, showPresets = true }: { onSelect: (value: Date[]) => void, defaultDate?: Date[], showPresets?: boolean }) {
  const { t, i18n } = useTranslation()
  const lastEmitted = useRef<Date[] | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)

  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [openUpward, setOpenUpward] = useState<boolean>(false)
  const [alignRight, setAlignRight] = useState<boolean>(false)
  const [displayDate, setDisplayDate] = useState<Date[]>(defaultDate || [new Date()])
  const [activePreset, setActivePreset] = useState<string | null>(null)
  const [viewMonth, setViewMonth] = useState<Date>(startOfDay(defaultDate?.[0] || new Date()))
  const [anchor, setAnchor] = useState<Date | null>(null)   // first click of a range, before the second
  const [hovered, setHovered] = useState<Date | null>(null)
  const [view, setView] = useState<'days' | 'months' | 'years'>('days')

  /**
   * The START keeps the older DateRangePicker's day-forward shift. That shift compensates for the
   * consumers formatting days with toISOString(), which rolls back a day in any positive-UTC-offset
   * zone - ChargerLogs turns the range into day strings to fetch production logs, and Charges
   * subtracts the day again. Dropping it here would silently move which days those pages request.
   *
   * The END deliberately differs: callers pass the last moment of the closing day rather than its
   * midnight. The old picker passed midnight, which toISOString() then rolled back, so the day the
   * user picked as the end was excluded from both the log fetch and the Charges filter. Ranges now
   * include their final day.
   */
  function emit(range: Date[]) {
    const shifted = range.map(d => new Date(d.getTime()))
    shifted[0].setDate(shifted[0].getDate() + 1)
    // The label shows what was actually chosen, not the shifted wire value - otherwise Last year
    // reads as starting a day after the date it really filters from. The parent echoes the shifted
    // range straight back through defaultDate, so remember it and ignore that one echo.
    lastEmitted.current = shifted
    setDisplayDate(range)
    onSelect(shifted)
  }

  function handlePreset(preset: { key: string, days: number }) {
    const end = new Date()
    const start = addDays(end, -preset.days)
    setActivePreset(preset.key)
    setAnchor(null)
    setHovered(null)
    setViewMonth(startOfDay(end))
    emit([start, end])
    setIsOpen(false)
  }

  function handleDayClick(day: Date) {
    setActivePreset(null)
    if (!anchor) { setAnchor(day); setHovered(null); return }
    const [from, to] = day < anchor ? [day, anchor] : [anchor, day]
    setAnchor(null)
    setHovered(null)
    // Close on the last moment of the end day, so picking a single day still covers that whole day.
    emit([from, new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59, 999)])
    setIsOpen(false)
  }

  // Reflect a range chosen elsewhere on the page (the prop name is historical), but skip the parent
  // echoing back the shifted range this component just emitted.
  useEffect(() => {
    if (!defaultDate) return
    const echoed = lastEmitted.current
      && lastEmitted.current.length === defaultDate.length
      && lastEmitted.current.every((d, i) => d.getTime() === defaultDate[i].getTime())
    if (!echoed) setDisplayDate(defaultDate)
  }, [defaultDate])

  // The trigger sits low on a phone, so a downward panel puts the calendar past the fold. Measure
  // once it is on screen and flip it above the trigger when that is where the room is. Likewise a
  // trigger at the right end of a row (the charger page's Daily meter readings card) would run the
  // panel past the edge, where a scrolling ancestor cuts it off; it is then anchored to the trigger's
  // right edge instead, when that leaves it on screen.
  useLayoutEffect(() => {
    if (!isOpen) { setOpenUpward(false); setAlignRight(false); setAnchor(null); setHovered(null); setView('days'); return }
    const el = panelRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    if (rect.bottom > window.innerHeight && rect.top - rect.height > 0) setOpenUpward(true)
    const span = visibleSpan(el)
    const trigger = el.parentElement?.getBoundingClientRect()
    if (trigger && rect.right > span.right && trigger.right - rect.width >= span.left) setAlignRight(true)
  }, [isOpen])

  const ref = useDetectClickOutside({
    onTriggered: (event: Event) => {
      // The hook listens for click on document and asks whether the panel still contains the target.
      // Stepping into the month or year view removes the very button that was clicked before the
      // event reaches document, so contains() is handed a detached node and reads an inside click as
      // an outside one - which closed the picker on every drill-down. A target that left the document
      // during its own event cannot be an outside click.
      const target = event.target as Node | null
      if (target && !target.isConnected) return
      setIsOpen(false)
    },
  })

  const weekStart = firstDayOfWeek(i18n.language)
  const days = buildMonthGrid(viewMonth, weekStart)
  const today = startOfDay(new Date())
  const monthName = new Intl.DateTimeFormat(i18n.language, { month: 'long' }).format(viewMonth)
  const monthNames = Array.from({ length: 12 }, (_, m) =>
    new Intl.DateTimeFormat(i18n.language, { month: 'short' }).format(new Date(2000, m, 1)))
  // Years are paged a dozen at a time, aligned so a page always starts on a multiple of 12.
  const YEAR_PAGE = 12
  const yearPageStart = Math.floor(viewMonth.getFullYear() / YEAR_PAGE) * YEAR_PAGE
  const years = Array.from({ length: YEAR_PAGE }, (_, i) => yearPageStart + i)

  function step(direction: number) {
    if (view === 'days') setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + direction, 1))
    else if (view === 'months') setViewMonth(new Date(viewMonth.getFullYear() + direction, viewMonth.getMonth(), 1))
    else setViewMonth(new Date(viewMonth.getFullYear() + direction * YEAR_PAGE, viewMonth.getMonth(), 1))
  }
  // 2023-01-01 was a Sunday, so offsetting it by (weekStart + i) lands on the right weekday. Basing
  // this on a Monday instead shifts every label one day, which is not visible in the grid below.
  const weekdayNames = Array.from({ length: 7 }, (_, i) =>
    new Intl.DateTimeFormat(i18n.language, { weekday: 'short' }).format(addDays(new Date(2023, 0, 1), (weekStart + i) % 7)))

  // Once the first endpoint is down the calendar shows only the range being built - the previously
  // applied range must stop painting, or its old start and band sit there alongside the new anchor.
  // The far end then follows the cursor, so the range is visible before it is committed.
  const previewFrom = anchor
    ? (hovered && hovered < anchor ? hovered : anchor)
    : (displayDate[0] ? startOfDay(displayDate[0]) : null)
  const previewTo = anchor
    ? (hovered && hovered > anchor ? hovered : anchor)
    : (displayDate[1] ? startOfDay(displayDate[1]) : null)

  return (
    <Container ref={ref}>
      <Trigger onClick={() => setIsOpen(!isOpen)}>
        <Icon path="general.calendar" width="20px" height="20px" margintop="10px" />
        <TriggerLabel>
          {displayDate[1]
            ? `${asDayLabel(displayDate[0])} - ${asDayLabel(displayDate[1])}`
            : asDayLabel(displayDate[0])}
        </TriggerLabel>
      </Trigger>

      {isOpen && (
        <Panel ref={panelRef} $up={openUpward} $alignRight={alignRight}>
          {/* ErrorLogs has its own preset dropdown beside the picker, so it opts out of these. */}
          {showPresets && (
            <PresetColumn>
              {PRESETS.map(preset => (
                <PresetButton key={preset.key} type="button" $selected={activePreset === preset.key}
                  onClick={() => handlePreset(preset)}>
                  {t(preset.key)}
                </PresetButton>
              ))}
            </PresetColumn>
          )}

          <CalendarColumn>
            <MonthHeader>
              <NavButton type="button" aria-label="previous" onClick={() => step(-1)}>
                <Icon path="general.backArrow" width="14px" height="14px" />
              </NavButton>
              <TitleRow>
                {view === 'days' && (
                  <TitleButton type="button" onClick={() => setView('months')}>{monthName}</TitleButton>
                )}
                {view === 'years' ? (
                  <TitleButton type="button" tabIndex={-1} style={{ cursor: 'default' }}>
                    {`${yearPageStart} - ${yearPageStart + YEAR_PAGE - 1}`}
                  </TitleButton>
                ) : (
                  <TitleButton type="button" onClick={() => setView('years')}>{viewMonth.getFullYear()}</TitleButton>
                )}
              </TitleRow>
              <NavButton type="button" aria-label="next" onClick={() => step(1)}>
                <Icon path="general.forwardArrow" width="14px" height="14px" />
              </NavButton>
            </MonthHeader>

            <GridArea>
              {view === 'days' && (
                <Grid>
                  {weekdayNames.map(name => <WeekdayCell key={name}>{name}</WeekdayCell>)}
                  {days.map(day => {
                    const isEdge = (!!previewFrom && sameDay(day, previewFrom)) || (!!previewTo && sameDay(day, previewTo)) || (!!anchor && sameDay(day, anchor))
                    const inRange = !!previewFrom && !!previewTo && day > previewFrom && day < previewTo
                    return (
                      <DayCell key={day.getTime()} type="button"
                        $outside={day.getMonth() !== viewMonth.getMonth()}
                        $edge={isEdge}
                        $inRange={inRange}
                        $today={sameDay(day, today)}
                        onMouseEnter={() => anchor && setHovered(day)}
                        onClick={() => handleDayClick(day)}>
                        {day.getDate()}
                      </DayCell>
                    )
                  })}
                </Grid>
              )}

              {view === 'months' && (
                <CoarseGrid>
                  {monthNames.map((name, m) => (
                    <CoarseCell key={name} type="button"
                      $selected={m === viewMonth.getMonth()}
                      $current={m === today.getMonth() && viewMonth.getFullYear() === today.getFullYear()}
                      onClick={() => { setViewMonth(new Date(viewMonth.getFullYear(), m, 1)); setView('days') }}>
                      {name}
                    </CoarseCell>
                  ))}
                </CoarseGrid>
              )}

              {view === 'years' && (
                <CoarseGrid>
                  {years.map(year => (
                    <CoarseCell key={year} type="button"
                      $selected={year === viewMonth.getFullYear()}
                      $current={year === today.getFullYear()}
                      onClick={() => { setViewMonth(new Date(year, viewMonth.getMonth(), 1)); setView('months') }}>
                      {year}
                    </CoarseCell>
                  ))}
                </CoarseGrid>
              )}
            </GridArea>
            {view === 'days' && anchor && <Hint>{asDayLabel(anchor)} &rarr; ...</Hint>}
          </CalendarColumn>
        </Panel>
      )}
    </Container>
  )
}
